import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  PlanSubmitBlockReason,
  type ProcurementPlanReceptionError,
  type ProcurementPlanSubmitErrorCategory,
} from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  PrimaryButton,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { GcExcludedKomaMark } from '~/components/common/GcExcludedKomaMark'
import {
  PlanStatusNote,
  RECEIPT_CHECK_SUSPENDED_NOTE,
  SEND_RETRYING_NOTE,
} from '~/components/common/PlanStatusNote'
import { PlanSubmissionStatusBadge } from '~/components/common/PlanSubmissionStatusBadge'
import {
  StatusHeaderCard,
  type StatusHeaderItem,
  statusHeaderValueStyle,
} from '~/components/planSubmit/StatusHeaderCard'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import {
  STATUS_BADGE_LABEL_KEYS,
  deriveStatusBadge,
} from '~/models/planSubmission/statusBadge'
import { showsSubmitBlockReasonText } from '~/models/planSubmission/submitBlockReason'
import { blockReasonView } from '~/pages/ProcurementPlanDetail/models/blockReasonView'
import type { SubmitAvailability } from '~/pages/ProcurementPlanDetail/models/canSubmit'
import { formatTimestamp } from '~/pages/ProcurementPlanDetail/models/formatTimestamp'

import { GcCreationAlignedKomaMark } from './GcCreationAlignedKomaMark'

type Props = {
  status: PlanSubmissionStatus
  bgCode: string
  bgName: string | undefined
  /** 作成日時。 */
  fileCreatedAt: Timestamp | undefined
  /** 最終提出日時。未提出なら undefined。 */
  submittedAt: Timestamp | undefined
  /** 提出者の member ID（AC-PPD-027）。表示名へ解決し、生 ID は出さない。 */
  submittedBy: string | undefined
  /** 前回提出（前世代）の提出日時。初回提出なら undefined。 */
  previousSubmittedAt: Timestamp | undefined
  /** 提出期限 (AC-PLC-020-1。backend 算出値)。期限を出さない断面 (当日・週間以降) は undefined。 */
  submissionDeadlineAt: Timestamp | undefined
  /** 提出中か（= mutation isPending）。ボタンを loading 表示にする。 */
  isSubmitting: boolean
  /** 提出ボタンを押せるかと、 押せない理由 (`resolveSubmitAvailability`)。 誘導メッセージと同じ値を渡す。 */
  submitAvailability: SubmitAvailability
  /** 「提出」ボタン押下（確認ダイアログを開く） */
  onSubmit: () => void
  /** 失敗原因の分類 (plansubmit-error-detail design AC-PLC-021 の再解釈)。 */
  errorCategory: ProcurementPlanSubmitErrorCategory
  /** OCCTO 受付エラー詳細 (差戻し時、BP エラーコード体系)。 */
  receptionErrors: ProcurementPlanReceptionError[]
  /** 内訳の件数の上限 (作成失敗の原因が上限超過の時だけ set)。 */
  breakdownRowLimit?: number
  /**
   * GC 経過による除外コマ番号 (gc-excluded-koma-visibility design AC-PLC-037-2、
   * 決定 4: 一覧と同じ chip をヘッダの status item にも置く。design gap)。
   * 空配列 (または未除外) なら chip は表示されない。
   */
  excludedKomaNumbers: number[]
  /** 作成時に GC 済みでそろえたコマ番号 (AC-PLC-037-2)。空なら chip は表示されない。提出ステータスでは絞らない。 */
  creationAlignedKomaNumbers: number[]
  /** OCCTO 呼び出し上限による受付照合停止中か (AC-PLC-058。backend 判定値をそのまま渡す)。 */
  receiptCheckSuspendedByCallLimit: boolean
  /** OCCTO と通信できず自動で再送中か (AC-PLC-036。backend 判定値をそのまま渡す)。 */
  sendRetrying: boolean
  /**
   * 利用者の提出を止める理由 (AC-PLC-055 / 056)。省略時は理由なし。AC-PLC-062 により、
   * 新しい版に置き換えられた版 (055) は提出ボタンを描画せず、登録済みの取引先の行が欠けた版
   * (056) は提出ボタンを非活性にして 056 の誘導メッセージを押せない理由の文として出す。
   */
  submitBlockReason?: PlanSubmitBlockReason
}

/**
 * 提出情報バーの「単位（kWh）」項目 (AC-PLC-018、plan-common.md §2 提出情報バー)。
 * 計画値の単位は kWh 固定なので値も固定。design `.sd-info-bar` には無い項目のため、
 * 見た目は他の項目と同じ `statusHeaderValueStyle` に合わせる。版の詳細と提出記録の無い
 * 計画の詳細 (AC-PPD-028) の両方が使う。
 */
export function buildUnitHeaderItem(t: TFunction): StatusHeaderItem {
  return {
    key: 'unit',
    label: t('page:procurementPlanDetail.header.unit'),
    value: (
      <span style={statusHeaderValueStyle}>
        {t('page:procurementPlanDetail.header.unitValue')}
      </span>
    ),
  }
}

/**
 * 需要調達計画詳細のヘッダ status block（design `ds-submit-meta` 相当）。
 *
 * カード枠・label+value グリッド・バッジ表示・タイムスタンプ行の共通構造は
 * `~/components/planSubmit/StatusHeaderCard` に集約（GenerationSalesPlanDetail と共有）。
 * 本ファイルはページ固有の項目定義（bgCode/Name の複合表示・提出ボタン）のみを持つ薄い形。
 *
 * defer: 計画提出者コード（sender_code）は detail proto に未提供のため表示しない
 * （design にはあるが今回スコープ外）。
 */
export const DetailHeader = ({
  status,
  bgCode,
  bgName,
  fileCreatedAt,
  submittedAt,
  submittedBy,
  previousSubmittedAt,
  submissionDeadlineAt,
  isSubmitting,
  submitAvailability,
  onSubmit,
  errorCategory,
  receptionErrors,
  breakdownRowLimit,
  excludedKomaNumbers,
  creationAlignedKomaNumbers,
  receiptCheckSuspendedByCallLimit,
  sendRetrying,
  submitBlockReason = PlanSubmitBlockReason.UNSPECIFIED,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const badge = deriveStatusBadge(status)
  // AC-PPD-027: 提出時刻が無ければ「—」、あれば表示名（未解決も「—」。生 ID は出さない）。
  const submitterText =
    (submittedAt !== undefined ? resolveDisplayName(submittedBy) : undefined) ??
    t('page:procurementPlanDetail.header.empty')
  const isSubmitBlocked =
    submitBlockReason !== PlanSubmitBlockReason.UNSPECIFIED
  // AC-PLC-062: 理由ごとの扱いは blockReasonView の表で決める。 この build が知らない理由は
  // 押せない理由の文を出せないので、 置き換えられた版 (055) と同じく描画しない側に倒す。
  const blockView = blockReasonView(submitBlockReason)
  const hidesSubmitButton =
    isSubmitBlocked && blockView?.submitButton !== 'disabled'
  // AC-PLC-020-2 注記 (055 / 056 共通): 送信失敗のまま提出を止める理由がある版は、
  // ステータスバッジを送信失敗に保ちつつ、再提出を促す通常の案内 (警告アイコンと
  // tooltip) を出さない (代わりに誘導メッセージが出る)。
  const suppressSendFailedGuidance =
    status === PlanSubmissionStatus.SEND_FAILED && isSubmitBlocked

  const timestampText = (v: Timestamp | undefined): string =>
    v !== undefined
      ? formatTimestamp(v)
      : t('page:procurementPlanDetail.header.empty')

  const items: StatusHeaderItem[] = [
    {
      key: 'status',
      label: t('page:procurementPlanDetail.header.status'),
      value: (
        <>
          <PlanSubmissionStatusBadge
            errorDetail={
              suppressSendFailedGuidance
                ? undefined
                : { errorCategory, receptionErrors, breakdownRowLimit }
            }
            hasWarning={badge.hasWarning && !suppressSendFailedGuidance}
            labelKeys={STATUS_BADGE_LABEL_KEYS}
            variant={badge.variant}
          />
          <PlanStatusNote
            {...RECEIPT_CHECK_SUSPENDED_NOTE}
            visible={receiptCheckSuspendedByCallLimit}
          />
          <PlanStatusNote {...SEND_RETRYING_NOTE} visible={sendRetrying} />
          <GcExcludedKomaMark excludedKomaNumbers={excludedKomaNumbers} />
          <GcCreationAlignedKomaMark
            alignedKomaNumbers={creationAlignedKomaNumbers}
          />
        </>
      ),
    },
    {
      key: 'bgCode',
      label: t('page:procurementPlanDetail.header.bgCode'),
      // [design 差異] 2026-08-28 デザイナー判断 (一覧の BG セル = PR #6758 /
      // #6699 と同基準)。 名称を薄字にせず本文と同じ濃さにし、 コードとの間隔を
      // 8px にする。 主従はコード側の fontWeight 600 + monospace で付いている
      // ので、 色を戻しても崩れない。
      // 一覧 (需要調達計画一覧) からこの画面へ遷移すると同じ BG が並んで見える
      // ため、 揃えないと同じ導線の中で濃さが変わる。
      // 8080 の plan/submission-detail.html は名称を sm 12px / mid-emphasis /
      // 4px で出しており、 本変更は 8080 から遠ざかる方向 (2026-08-28 判断の横展開)。
      value: (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          <span
            style={{
              ...statusHeaderValueStyle,
              fontFamily: 'monospace',
              fontWeight: 600,
            }}
          >
            {bgCode}
          </span>
          {bgName !== undefined && bgName !== '' && (
            <span
              style={{
                fontSize: fontSizes.md.value,
                color: LightModeSemanticColors.Text.highEmphasis.value,
              }}
            >
              {bgName}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'created',
      label: t('page:procurementPlanDetail.header.created'),
      value: (
        <span style={statusHeaderValueStyle}>
          {timestampText(fileCreatedAt)}
        </span>
      ),
    },
    {
      key: 'lastSubmitted',
      label: t('page:procurementPlanDetail.header.lastSubmitted'),
      value: (
        <span style={statusHeaderValueStyle}>{timestampText(submittedAt)}</span>
      ),
    },
    {
      key: 'submittedBy',
      label: t('page:procurementPlanDetail.header.submittedBy'),
      value: <span style={statusHeaderValueStyle}>{submitterText}</span>,
    },
    {
      key: 'previousSubmitted',
      label: t('page:procurementPlanDetail.header.previousSubmitted'),
      value: (
        <span style={statusHeaderValueStyle}>
          {timestampText(previousSubmittedAt)}
        </span>
      ),
    },
    {
      key: 'submissionDeadline',
      label: t('page:procurementPlanDetail.header.submissionDeadline'),
      value: (
        <span style={statusHeaderValueStyle}>
          {timestampText(submissionDeadlineAt)}
        </span>
      ),
    },
    buildUnitHeaderItem(t),
  ]

  const submitButton = (
    <PrimaryButton
      disabled={!submitAvailability.enabled}
      loading={isSubmitting}
      onClick={onSubmit}
      size="sm"
    >
      {t('page:procurementPlanDetail.submit.buttonLabel')}
    </PrimaryButton>
  )

  // 押せない理由 (順序は `resolveSubmitAvailability`)。
  // 非活性の時だけ、 ボタンの直下に右端をそろえて文で常に出す (ヘッダーは項目で横が埋まるため。
  // design `plan/submission-detail.html` の #sd-submit-reason)。 ツールチップは補助
  const statusDisabledReason =
    submitAvailability.disabledReasonKey === undefined
      ? undefined
      : t(submitAvailability.disabledReasonKey)
  // AC-WFW-014: 閲覧のみ (完了済み・削除済みのワークフローから開いた) の時は、画面固有の
  // 理由より優先して読み取り専用の理由だけをツールチップで出す (常時文は出さない。
  // 版のデータの不備による理由は例外で、常時文も残す)。取得中・失敗中は readonlyReason が無く、
  // 従来の画面固有の理由に戻る。
  const { readonlyReason } = useWorkflowLockReasons()
  const submitDisabledReason = readonlyReason ?? statusDisabledReason

  return (
    <StatusHeaderCard
      action={
        hidesSubmitButton ? null : (
          <VStack alignItems="flex-end" gap={1} maxWidth="28em">
            {submitDisabledReason !== undefined ? (
              <ButtonTooltip content={submitDisabledReason}>
                {submitButton}
              </ButtonTooltip>
            ) : (
              submitButton
            )}
            {/* 受領済は文を出さない (ツールチップのみ。 showsSubmitBlockReasonText)。
                閲覧のみの間は、版のデータの不備 (未入力のコマ・056) だけ文で残す */}
            {statusDisabledReason !== undefined &&
              (readonlyReason === undefined ||
                submitAvailability.disabledReasonIsDataDefect) &&
              showsSubmitBlockReasonText(status) && (
                <ActionReasonText textAlign="right">
                  {statusDisabledReason}
                </ActionReasonText>
              )}
          </VStack>
        )
      }
      items={items}
    />
  )
}
